import React from 'react'
import type { AccessContext } from '../../../types/access'
import type { AdminTab } from './navigationRegistry'
import { GuidedSidePanel, type GuidedWorkflowDefinition } from './GuidedWorkflow'
import { useI18n } from '../../../i18n/I18nContext'

const currentAfter = (complete: boolean | undefined) => complete ? 'complete' as const : 'current' as const

export const AdminProcessGuide: React.FC<{
  tab: AdminTab
  accessContext: AccessContext | null
}> = ({ tab, accessContext }) => {
  const { t } = useI18n()
  const setup = accessContext?.setupState
  const relationships = accessContext?.relationshipAccess || []
  let workflow: GuidedWorkflowDefinition | null = null

  if (tab === 'jobs') workflow = {
    id: 'workforce-demand',
    title: t('Post and fill work'),
    description: t('Post Work creates the canonical Job and Shift together. Continue with the actual applications, matches, and assignments returned below.'),
    steps: [
      { id: 'post', title: t('Post Work'), status: currentAfter(setup?.hasJobs) },
      { id: 'shift', title: t('Add Shift'), status: setup?.hasJobs ? 'current' : 'todo' },
      { id: 'fill', title: t('Fill Shift'), status: setup?.hasAssignments ? 'complete' : 'todo' },
      { id: 'applications', title: t('Review Applications / Choose Workers'), status: 'todo' },
    ],
  }
  if (tab === 'schedule') workflow = {
    id: 'schedule-coverage',
    title: t('Cover upcoming work'),
    description: t('Use Schedule for dated coverage. Post new demand through Post Work; add a shift here only for an existing job.'),
    steps: [
      { id: 'add', title: t('Add Shift'), status: setup?.hasJobs ? 'current' : 'blocked', blockingReason: setup?.hasJobs === false ? t('Post work before adding another shift.') : undefined },
      { id: 'fill', title: t('Fill Shift'), status: setup?.hasAssignments ? 'complete' : 'todo' },
      { id: 'conflict', title: t('Resolve Conflict'), status: 'todo', description: t('Open a shift to use its backend-reported conflict and warning state.') },
    ],
  }

  if (tab === 'workers') workflow = {
    id: 'workers',
    title: t('Build your workforce'),
    description: t('Add or invite people, then review the profile and compliance evidence the backend makes available in your scope.'),
    steps: [
      { id: 'add', title: t('Add Worker'), status: currentAfter(setup?.hasWorkers), description: t('Create a worker record or connect an existing global worker profile.') },
      { id: 'invite', title: t('Invite Workers'), status: setup?.hasWorkers ? 'current' : 'todo', description: t('Send an invitation using the worker controls below.') },
      { id: 'compliance', title: t('Review Worker Compliance'), status: 'todo', description: t('Open the worker profile and review actual document and consent states.') },
    ],
  }
  if (tab === 'locations') workflow = {
    id: 'worksite',
    title: t('Add a worksite and Punch Area'),
    description: t('A publish-ready worksite needs a canonical address, map position, valid IANA timezone, and the required punch boundary.'),
    steps: [
      { id: 'location', title: t('Add Worksite'), status: currentAfter(setup?.hasLocation) },
      { id: 'geofence', title: t('Configure Punch Area'), status: setup?.hasGeofence ? 'complete' : setup?.hasLocation ? 'current' : 'todo', blockingReason: setup?.hasLocation === false ? t('Add the worksite before configuring its Punch Area.') : undefined },
      { id: 'review', title: t('Review punch readiness'), status: setup?.hasGeofence ? 'current' : 'todo', description: t('Use the readiness and validation state returned for the selected worksite.') },
    ],
  }
  if (tab === 'relationships') workflow = {
    id: 'partner-access',
    title: t('Connect a Workforce Partner'),
    description: t('Create the relationship, configure its backend policy, then grant only the worksites the partner needs.'),
    steps: [
      { id: 'connect', title: t('Connect Workforce Partner'), status: relationships.length ? 'complete' : 'current' },
      { id: 'policy', title: t('Configure Relationship Access'), status: relationships.length ? 'current' : 'todo' },
      { id: 'sites', title: t('Grant Sites / Locations'), status: 'todo', description: t('Site grants determine where delegated jobs and shifts are allowed.') },
    ],
  }
  if (tab === 'users-access') workflow = {
    id: 'supervisor-access',
    title: t('Add a Supervisor'),
    description: t('Invite the user, grant the smallest useful scope, then confirm the resolved access context.'),
    steps: [
      { id: 'invite', title: t('Add Supervisor'), status: currentAfter(setup?.hasSupervisors) },
      { id: 'scope', title: t('Configure User Scope'), status: setup?.hasSupervisors ? 'current' : 'todo' },
      { id: 'explain', title: t('Explain what the user can see'), status: 'todo', description: t('Use the permission diagnostics below; mode never grants access.') },
    ],
  }
  return workflow ? <GuidedSidePanel workflow={workflow} /> : null
}
